// src/modules/kinema/layeredMediaService.ts
import { httpRequest } from '@services/http';
import { Job } from '@models/job.model';
import { addJob } from '@store/jobTrackerStore';

const BASE_URL = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/content/layered_media`;

export interface LayeredMedia {
  id: number;
  name: string;
  type: 'image' | 'video';
  organization_id: number;
  data: any;
  is_standalone?: boolean;
  preview_media_id?: number | null;
  preview_url?: string | null;
}

export type StandaloneLayeredMedia = LayeredMedia;

export interface LayeredMediaRender {
  id: number;
  layered_media_id: number;
  media_id: number | null;
  status: 'queued' | 'rendering' | 'succeeded' | 'failed';
  error_message: string;
}

export async function getLayeredMedia(id: number): Promise<LayeredMedia> {
  return httpRequest<LayeredMedia>(`${BASE_URL}/${id}/`, { method: 'GET' });
}

export async function createLayeredMedia(payload: {
  name: string;
  organization_id: number;
  data: any;
  type?: 'image' | 'video';
  is_standalone?: boolean;
}): Promise<LayeredMedia> {
  return httpRequest<LayeredMedia>(`${BASE_URL}/`, {
    method: 'POST',
    body: payload,
  });
}

export async function updateLayeredMedia(id: number, payload: {
  name?: string;
  data?: any;
  preview_media_id?: number;
  /** Not composition data: the user confirmed editing an approved/published ad's
   *  composition, so the server skips the content lock for this request only. */
  force_edit?: boolean;
}): Promise<LayeredMedia> {
  return httpRequest<LayeredMedia>(`${BASE_URL}/${id}/`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function deleteLayeredMedia(id: number): Promise<void> {
  await httpRequest(`${BASE_URL}/${id}/`, { method: 'DELETE' });
}

export async function convertLegacyLayer(layerId: string): Promise<LayeredMedia> {
  return httpRequest<LayeredMedia>(`${BASE_URL}/convert/${layerId}/`, {
    method: 'POST',
  });
}

export interface DesignSummary {
  id: number;
  name: string;
  /** What to show: the ad's name when it has one, the design's own otherwise. */
  label: string;
  type: string;
  is_standalone: boolean;
  preview_url: string | null;
  /** The composition's own pixel size. Null when it has no root layer to measure. */
  width?: number | null;
  height?: number | null;
}

/**
 * Every design in an organization, with a thumbnail and without the composition itself.
 *
 * Separate from `listStandaloneLayeredMedia` on purpose: that one returns Create Studio
 * projects only, which is almost none of them, and each row carries the whole layered
 * document. This is what a picker wants.
 */
export async function listOrgDesigns(
  organizationId: number,
  search?: string,
  opts?: { landscape?: boolean },
): Promise<DesignSummary[]> {
  // Capped server-side. One organization on prod has 8,320 designs, so this is a picker
  // showing the most recent, with search for anything older.
  //
  // `landscape` is applied server-side for the same reason: the cap has to fall on the
  // designs that fit the card, not on everything. Filtering here would thin a page of 24
  // down to whatever happened to survive.
  const query = search ? `&search=${encodeURIComponent(search)}` : '';
  const shape = opts?.landscape ? '&landscape=true' : '';
  return httpRequest<DesignSummary[]>(
    `${BASE_URL}/designs/?organization_id=${organizationId}${query}${shape}`,
    { method: 'GET' },
  );
}

export async function listStandaloneLayeredMedia(organizationId: number): Promise<StandaloneLayeredMedia[]> {
  return httpRequest<StandaloneLayeredMedia[]>(
    `${BASE_URL}/?organization_id=${organizationId}&is_standalone=true`,
    { method: 'GET' },
  );
}

export async function updateLayeredMediaPreview(
  id: number,
  previewMediaId: number,
): Promise<StandaloneLayeredMedia> {
  return httpRequest<StandaloneLayeredMedia>(`${BASE_URL}/${id}/`, {
    method: 'PATCH',
    body: { preview_media_id: previewMediaId },
  });
}

export async function createLayeredMediaRender(
  id: number,
  mediaId?: number,
): Promise<LayeredMediaRender> {
  return httpRequest<LayeredMediaRender>(`${BASE_URL}/${id}/renders/`, {
    method: 'POST',
    body: mediaId != null ? { media_id: mediaId } : {},
  });
}

export async function listLayeredMediaRenders(id: number): Promise<LayeredMediaRender[]> {
  return httpRequest<LayeredMediaRender[]>(`${BASE_URL}/${id}/renders/`, {
    method: 'GET',
  });
}

export async function spawnLayeredMediaRender(id: number): Promise<Job> {
  const job = await httpRequest<Job>(`${BASE_URL}/${id}/render/`, {
    method: 'POST',
    body: {},
  });
  addJob(job);
  return job;
}

export async function duplicateLayeredMedia(id: number): Promise<StandaloneLayeredMedia> {
  return httpRequest<StandaloneLayeredMedia>(`${BASE_URL}/${id}/duplicate/`, {
    method: 'POST',
  });
}
